/**
 * Attendance shift metadata & helpers.
 *
 * Records can be stored under two shifts:
 *  - `corporate` (10:00–19:00) — legacy default. Data lives in
 *      `late_1000_1015`, `late_1015_1030`, `late_after_1030`,
 *      `early_1800_1830`, `early_1830_1900`, `early_before_1800`
 *    and (for chat-classifier uploads) is mirrored to the shift-agnostic
 *    `late_slab_1/2/3` + `early_slab_1/2/3` fields as well.
 *  - `firmware` (09:00–18:00) — data lives ONLY in the shift-agnostic
 *    `late_slab_1/2/3` + `early_slab_1/2/3` fields.
 *
 * All employee-facing views read via `getSlab*` helpers below so labels
 * and values track the record's shift correctly.
 */

export const SHIFT_META = {
    corporate: {
        key: "corporate",
        label: "Corporate Shift",
        window: "10:00 – 19:00",
        short: "10-7",
        lateLabels: ["10:00–10:15", "10:15–10:30", "After 10:30"],
        earlyLabels: ["18:00–18:30", "18:30–19:00", "Before 18:00"],
        lateShort: ["10–10:15", "10:15–10:30", "After 10:30"],
        earlyShort: ["18–18:30", "18:30–19", "Before 18"],
    },
    firmware: {
        key: "firmware",
        label: "Firmware Shift",
        window: "09:00 – 18:00",
        short: "9-6",
        lateLabels: ["09:00–09:15", "09:15–09:30", "After 09:30"],
        earlyLabels: ["17:00–17:30", "17:30–18:00", "Before 17:00"],
        lateShort: ["9–9:15", "9:15–9:30", "After 9:30"],
        earlyShort: ["17–17:30", "17:30–18", "Before 17"],
    },
};

export const shiftKeyOf = (record) => {
    const k = String(record?.shift || "corporate").toLowerCase();
    return k === "firmware" ? "firmware" : "corporate";
};

export const shiftMetaOf = (record) => SHIFT_META[shiftKeyOf(record)];

/** Get the three late-slab values for a record (shift-agnostic first,
 * falls back to legacy corporate fields for older uploads). */
export const getLateSlabs = (r) => {
    if (!r) return [0, 0, 0];
    const l1 = r.late_slab_1 ?? r.late_1000_1015 ?? 0;
    const l2 = r.late_slab_2 ?? r.late_1015_1030 ?? 0;
    const l3 = r.late_slab_3 ?? r.late_after_1030 ?? 0;
    return [Number(l1) || 0, Number(l2) || 0, Number(l3) || 0];
};

/** Get the three early-slab values for a record. */
export const getEarlySlabs = (r) => {
    if (!r) return [0, 0, 0];
    const e1 = r.early_slab_1 ?? r.early_1800_1830 ?? 0;
    const e2 = r.early_slab_2 ?? r.early_1830_1900 ?? 0;
    const e3 = r.early_slab_3 ?? r.early_before_1800 ?? 0;
    return [Number(e1) || 0, Number(e2) || 0, Number(e3) || 0];
};

export const sumLate = (r) => getLateSlabs(r).reduce((a, b) => a + b, 0);
export const sumEarly = (r) => getEarlySlabs(r).reduce((a, b) => a + b, 0);

/** For a given record, return the six per-slab labels appropriate to its
 * shift. Late[0..2] then Early[0..2]. */
export const getSlabLabels = (r) => {
    const m = shiftMetaOf(r);
    return { late: m.lateLabels, early: m.earlyLabels };
};

/** Preferred storage field to write to for a given legacy field, based on
 * the record's shift. Used by inline-edit so firmware records update the
 * shift-agnostic slab, not the corporate legacy field. */
export const editFieldFor = (record, legacyField) => {
    const shift = shiftKeyOf(record);
    if (shift === "corporate") return legacyField;
    const map = {
        late_1000_1015: "late_slab_1",
        late_1015_1030: "late_slab_2",
        late_after_1030: "late_slab_3",
        early_1800_1830: "early_slab_1",
        early_1830_1900: "early_slab_2",
        early_before_1800: "early_slab_3",
    };
    return map[legacyField] || legacyField;
};
